import React from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { Logo } from "../Logo";
import { LanguageSwitcher } from "../LanguageSwitcher";
import { useI18n } from "../../i18n/I18nContext";
import { useAuth } from "../../contexts/AuthContext";
import {
  LayoutDashboard, FolderKanban, KeyRound, LifeBuoy,
  FolderLock, Bell, LogOut, ShieldCheck, Radio, Inbox,
} from "lucide-react";

const NAV = [
  { to: "/dashboard", icon: LayoutDashboard, key: "dashboard.overview", end: true, id: "overview" },
  { to: "/dashboard/projects", icon: FolderKanban, key: "dashboard.projects", id: "projects" },
  { to: "/dashboard/licenses", icon: KeyRound, key: "dashboard.licenses", id: "licenses" },
  { to: "/dashboard/tickets", icon: LifeBuoy, key: "dashboard.tickets", id: "tickets" },
  { to: "/dashboard/documents", icon: FolderLock, key: "dashboard.documents", id: "documents" },
  { to: "/dashboard/sygnal-vault", icon: Radio, key: "nav.vault", id: "sygnal-vault" },
  { to: "/dashboard/inquiries", icon: Inbox, key: "nav.inbox", id: "inquiries", adminOnly: true },
  { to: "/dashboard/notifications", icon: Bell, key: "dashboard.notifications", id: "notifications" },
];

export const DashboardLayout = ({ children }) => {
  const { t } = useI18n();
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const handleLogout = async () => {
    await logout();
    navigate("/login");
  };

  return (
    <div className="min-h-screen bg-[#050505] text-white flex">
      {/* Sidebar */}
      <aside
        className="hidden lg:flex w-64 flex-col fixed inset-y-0 left-0 border-r border-white/[0.08] bg-[#0A0D14]/50 backdrop-blur-xl z-30"
        data-testid="dashboard-sidebar"
      >
        <div className="p-6 border-b border-white/[0.08]">
          <Link to="/" data-testid="dashboard-logo-link"><Logo size={28} /></Link>
          <p className="mt-3 text-[10px] font-mono uppercase tracking-[0.25em] text-white/40">
            Command Center
          </p>
        </div>

        <nav className="flex-1 px-3 py-6 space-y-1">
          {NAV.filter((item) => !item.adminOnly || (user && user.role === "admin")).map((item) => {
            const Icon = item.icon;
            return (
              <NavLink
                key={item.id}
                to={item.to}
                end={item.end}
                data-testid={`sidebar-link-${item.id}`}
                className={({ isActive }) =>
                  `flex items-center gap-3 px-3 py-2.5 text-sm rounded-sm transition-colors ${
                    isActive
                      ? "bg-[#19A7E0]/10 text-[#19A7E0] border-l-2 border-[#19A7E0]"
                      : "text-white/70 hover:bg-white/5 hover:text-white border-l-2 border-transparent"
                  }`
                }
              >
                <Icon className="w-4 h-4" />
                <span>{t(item.key)}</span>
              </NavLink>
            );
          })}
        </nav>

        <div className="p-4 border-t border-white/[0.08] space-y-3">
          {user && typeof user === "object" && (
            <div className="px-3 py-2 bg-[#121620] rounded-sm">
              <p className="text-xs text-white/50">{t("dashboard.welcome")}</p>
              <p className="text-sm font-medium truncate" data-testid="user-name">{user.name}</p>
              <p className="text-[10px] font-mono text-white/40 truncate">{user.email}</p>
              <p className="mt-2 inline-flex items-center gap-1.5 text-[10px] font-mono uppercase tracking-[0.18em] text-[#19A7E0]">
                <ShieldCheck className="w-3 h-3" /> MFA {user.mfa_enabled ? "ON" : "OFF"}
              </p>
            </div>
          )}
          <button
            onClick={handleLogout}
            data-testid="logout-btn"
            className="w-full flex items-center justify-center gap-2 px-3 py-2 btn-ghost text-xs font-mono uppercase tracking-[0.18em] rounded-sm"
          >
            <LogOut className="w-3.5 h-3.5" /> {t("nav.logout")}
          </button>
        </div>
      </aside>

      {/* Main */}
      <main className="flex-1 lg:ml-64">
        <div className="lg:hidden sticky top-0 z-20 bg-[#050505]/90 backdrop-blur-xl border-b border-white/[0.08] px-6 py-3 flex items-center justify-between">
          <Logo size={26} />
          <div className="flex items-center gap-2">
            <LanguageSwitcher />
            <button
              onClick={handleLogout}
              data-testid="logout-btn-mobile"
              className="p-2 text-white/80"
              aria-label="logout"
            >
              <LogOut className="w-4 h-4" />
            </button>
          </div>
        </div>
        <div className="hidden lg:flex sticky top-0 z-20 px-12 py-4 bg-[#050505]/80 backdrop-blur-xl border-b border-white/[0.08] items-center justify-end gap-3">
          <LanguageSwitcher />
        </div>
        {/* Mobile bottom nav */}
        <nav className="lg:hidden fixed bottom-0 left-0 right-0 z-30 bg-[#0A0D14] border-t border-white/[0.08] flex" data-testid="mobile-bottom-nav">
          {NAV.slice(0, 5).map((item) => {
            const Icon = item.icon;
            return (
              <NavLink
                key={item.id}
                to={item.to}
                end={item.end}
                data-testid={`mobile-nav-${item.id}`}
                className={({ isActive }) =>
                  `flex-1 flex flex-col items-center justify-center py-2.5 text-[10px] font-mono uppercase tracking-wider ${
                    isActive ? "text-[#19A7E0]" : "text-white/50"
                  }`
                }
              >
                <Icon className="w-4 h-4 mb-1" />
                {t(item.key)}
              </NavLink>
            );
          })}
        </nav>

        <div className="px-6 lg:px-12 py-8 pb-24 lg:pb-12">{children}</div>
      </main>
    </div>
  );
};

export default DashboardLayout;
